import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { Card } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import { formatSuccessRate } from "@/utils/success-rate";
import { useTopThemes } from "../hooks/useTopThemes";

interface TopThemesListProps {
  title: string;
  subtitle: string;
  items: { key: string; name: string; value: string }[];
  isLoading: boolean;
}

const TopThemesList = ({
  title,
  subtitle,
  items,
  isLoading,
}: TopThemesListProps) => {
  const { t } = useTranslation();

  return (
    <Card className="p-4 flex-1 w-full flex flex-col gap-3">
      <div>
        <p className="font-semibold">{title}</p>
        <p className="text-sm text-gray-500">{subtitle}</p>
      </div>
      {isLoading ? (
        <div className="flex flex-col gap-2">
          {Array.from({ length: 5 }).map((_, i) => (
            <Skeleton key={i} className="h-8 w-full" />
          ))}
        </div>
      ) : items.length === 0 ? (
        <p className="text-sm text-gray-500 py-4">
          {t("modules.stats.part2.no_data")}
        </p>
      ) : (
        <ol className="flex flex-col gap-2">
          {items.map((item, index) => (
            <li
              key={item.key}
              className="flex items-center gap-3 text-sm border-b last:border-b-0 pb-2 last:pb-0"
            >
              <span className="flex items-center justify-center size-6 rounded-full bg-gray-100 text-gray-600 font-semibold shrink-0">
                {index + 1}
              </span>
              <span className="flex-1 font-medium truncate">{item.name}</span>
              <span className="font-semibold text-primary">{item.value}</span>
            </li>
          ))}
        </ol>
      )}
    </Card>
  );
};

export const TopPopularThemes = () => {
  const { t } = useTranslation();
  const { data, isLoading } = useTopThemes("most_solved");
  const items = useMemo(
    () =>
      (data ?? []).map((d) => ({
        key: d.topic_id,
        name: d.topic_name,
        value: String(d.resolved_count),
      })),
    [data],
  );

  return (
    <TopThemesList
      title={t("modules.stats.part2.top_popular.title")}
      subtitle={t("modules.stats.part2.top_popular.subtitle")}
      items={items}
      isLoading={isLoading}
    />
  );
};

export const TopLeastUnderstoodThemes = () => {
  const { t } = useTranslation();
  const { data, isLoading } = useTopThemes("least_understood");
  const items = useMemo(
    () =>
      (data ?? []).map((d) => ({
        key: d.topic_id,
        name: d.topic_name,
        value: formatSuccessRate(d.success_rate),
      })),
    [data],
  );

  return (
    <TopThemesList
      title={t("modules.stats.part2.top_least_understood.title")}
      subtitle={t("modules.stats.part2.top_least_understood.subtitle")}
      items={items}
      isLoading={isLoading}
    />
  );
};
